/**
 * Builds Body, Rows, and Factory Match steps from raw PDM `products[]`.
 *
 * ## Hybrid data ownership (not “zero Dato”)
 *
 * - **PDM owns** Body / Rows / Factory Match **option lists** and part numbers.
 * - **Dato still owns** design/style steps, row tile images, some SKU overlays,
 *   and Factory Match presentation assets (`allPdmAssets`).
 * - A few vehicles use **code overrides** (see docs companion OVERRIDES.md).
 *
 * ## Strict mode
 *
 * Missing products, MMY matches, or required options throw — no silent Dato fallbacks
 * for those option lists.
 */

import type { Step } from "@views/Upholstery/types"
import type { PdmAssetRecord } from "../../types"
import { flattenMatchingApplications } from "../../pdm/normalizePdm"
import { resolveSeatDiagramDisplayMode } from "../../constants/seat-diagram-schematic-labels"
import { fetchPdmAssetRowConfigurations } from "../data/fetchPdmAssetRows"
import {
  dedupeFlatRowsForFactoryMatch,
  parseSelectedYear,
} from "./pdm-option-factories"
import {
  assembleMergedSteps,
  buildBodyStepFromOptions,
  buildFactoryMatchStepShell,
  buildRowsStepFromOptions,
  extractBodyAndRowOptionsFromFlat,
  filterRowAssetsForOptions,
} from "./buildBodyAndRowsFromPdm"
import { buildFactoryMatchSlugMaps } from "./factory-match/factoryMatchSlugs"
import {
  aggregateFactoryMatches,
  collectMiddlesByPartNumber,
  collectTrimsByPartNumber,
} from "./factory-match/aggregateFactoryMatches"
import { writePdmMergeDebugSnapshot } from "./writePdmMergeDebugSnapshot"
import { logPdmVehicleInspect } from "../debug/logPdmVehicleInspect"
import { resolveRowCountChoiceDefault } from "../../pdm/compareSeatingRowToInferred"

export interface UpholsteryVehicleContext {
  make: string
  model: string
  /** Single model year entered in YMM picker, e.g. `"2024"`. */
  years: string
}

export async function mergeConfigurableStepsFromPdmProducts(
  datoSteps: Step[],
  vehicleCtx: UpholsteryVehicleContext,
  products: Record<string, unknown>[]
): Promise<Step[]> {
  if (!products.length) {
    throw new Error(
      "❌ [PDM V2 STRICT] No PDM products available. PDM V2 is enabled but PDM API returned no products. Check PDM API connection, brand codes, and API token."
    )
  }

  const yearNum = parseSelectedYear(vehicleCtx.years)

  const flat = flattenMatchingApplications(
    products,
    vehicleCtx.make,
    vehicleCtx.model,
    yearNum
  )

  if (!flat.length) {
    throw new Error(
      `❌ [PDM V2 STRICT] No matching applications found for ${yearNum} ${vehicleCtx.make} ${vehicleCtx.model}. PDM products exist but none match this Make/Model/Year. Check PDM product data and application mappings.`
    )
  }

  const opts = extractBodyAndRowOptionsFromFlat(flat)

  let datoPdmAssetsRaw: PdmAssetRecord[] | null = null
  datoPdmAssetsRaw = await fetchPdmAssetRowConfigurations()

  const factoryMatchDatoAsset =
    datoPdmAssetsRaw?.find(
      (asset) =>
        asset.title?.toLowerCase().includes("factory match") ||
        asset.key?.toLowerCase().includes("factory-match") ||
        asset.key?.toLowerCase().includes("factorymatch")
    ) ?? null

  const datoBodyStep = datoSteps.find((s) => s.key === "body")
  const datoRowsStep = datoSteps.find((s) => s.key === "rows")

  const flatForFactoryMatch = dedupeFlatRowsForFactoryMatch(flat)

  const factoryMatchSlugMaps = buildFactoryMatchSlugMaps(
    opts.trimOpts,
    opts.bodyTypeOpts,
    opts.drvOpts,
    opts.pasOpts,
    opts.frontOpts,
    opts.backOpts,
    opts.midOpts
  )

  const seatDiagramLabelContext = {
    selectedMake: vehicleCtx.make,
    selectedModel: vehicleCtx.model,
    displayMode: resolveSeatDiagramDisplayMode(
      datoSteps,
      vehicleCtx.make,
      vehicleCtx.model
    ),
  }

  const { factoryMatches: fm, filterConfig: factoryMatchFilter } =
    aggregateFactoryMatches(
      flatForFactoryMatch,
      factoryMatchSlugMaps,
      { backOpts: opts.backOpts, frontOpts: opts.frontOpts },
      seatDiagramLabelContext,
      factoryMatchDatoAsset,
      collectTrimsByPartNumber(flat),
      collectMiddlesByPartNumber(flat)
    )

  logPdmVehicleInspect(
    products,
    vehicleCtx.make,
    vehicleCtx.model,
    yearNum,
    flat,
    opts.bodyTypeOpts,
    fm
  )

  const validationErrors: string[] = []

  if (opts.trimOpts.length === 0)
    validationErrors.push("Body > Trim options are empty (from PDM)")
  if (opts.bodyTypeOpts.length === 0)
    validationErrors.push("Body > Body Type options are empty (from PDM)")
  if (opts.drvOpts.length === 0)
    validationErrors.push("Body > Driver Seat options are empty (from PDM)")
  if (opts.pasOpts.length === 0)
    validationErrors.push("Body > Passenger Seat options are empty (from PDM)")
  if (opts.frontOpts.length === 0)
    validationErrors.push("Rows > Front Row options are empty (from PDM)")
  if (opts.backOpts.length === 0)
    validationErrors.push("Rows > Back Row options are empty (from PDM)")
  if (fm.length === 0)
    validationErrors.push("Factory Match options are empty (from PDM products)")

  if (validationErrors.length > 0) {
    const errorMsg = `❌ [PDM V2 STRICT] Failed to extract required options:\n  - ${validationErrors.join(
      "\n  - "
    )}\n\nCheck PDM product data for Body/Rows/Factory Match.`

    throw new Error(errorMsg)
  }

  const bodyStep = buildBodyStepFromOptions(opts, datoBodyStep)

  let datoPdmAssetsFiltered: PdmAssetRecord[] | null = null
  if (datoPdmAssetsRaw) {
    datoPdmAssetsFiltered = filterRowAssetsForOptions(datoPdmAssetsRaw, opts)
  }

  const rowsStep = buildRowsStepFromOptions(
    opts,
    datoRowsStep,
    datoPdmAssetsFiltered,
    resolveRowCountChoiceDefault(flat)
  )

  const factoryMatchStep = buildFactoryMatchStepShell(fm, factoryMatchFilter)

  const result = assembleMergedSteps({
    bodyStep,
    rowsStep,
    factoryMatchStep,
    datoSteps,
  })

  writePdmMergeDebugSnapshot({
    products,
    flat,
    opts,
    vehicleCtx,
    datoPdmAssetsRaw,
    datoPdmAssetsFiltered,
    factoryMatchDatoAsset,
    factoryMatches: fm,
    datoSteps,
    mergedSteps: result,
  })

  return result
}
